'use client';

import React, {FC, PropsWithChildren} from 'react';
import cn from 'classnames';
import Image from 'next/image';
import {useRouter} from 'next/navigation';

import {Button} from '@zoefin/design-system';

import {addToDataLayer} from '../../../../utils/pageUtils';

import AsFeatured from './AsFeatured';
import {BrandIcons, HeroProps} from './index.d';
import VideoPlayer from './VideoPlayer';

import styles from './styles.module.scss';

interface CalculatorLabelProps {
  description: string;
  className: string;
  label: string;
}

const CALCULATOR_LABELS: Record<
  'retirement' | 'investment',
  CalculatorLabelProps
> = {
  retirement: {
    description:
      "Do you know how much you need to retire? Zoe's free Retirement Calculator will reveal if you’re on track towards your dream retirement.",
    className: styles.hero__text_description__modRetirement,
    label: 'Calculate Your Retirement',
  },
  investment: {
    description:
      'How much will your money grow? Calculate the future value of your investments, your portfolio growth, and increase your ROI with our investment calculator.',
    className: styles.hero__text_description__modInvestment,
    label: 'Calculate Your Investments',
  },
};

const DEFAULT_BRAND_ICONS: BrandIcons[] = [];

const Hero: FC<PropsWithChildren<HeroProps>> = ({
  heroImage,
  descriptionText,
  brandIcons = DEFAULT_BRAND_ICONS,
  children,
  titleText = 'Your Way to Wealth',
  layoutType = 'layout-image',
}) => {
  const router = useRouter();

  const hasHeroImage = heroImage && !!Object.values(heroImage).length;
  const isHeroImageEnabled = heroImage !== false;

  let calculatorMobileLabel: string;

  const {retirement, investment} = CALCULATOR_LABELS;
  const isRetirement =
    CALCULATOR_LABELS.retirement.description === descriptionText;
  const isInvestment =
    CALCULATOR_LABELS.investment.description === descriptionText;

  if (isRetirement) {
    calculatorMobileLabel = retirement.label;
  } else if (isInvestment) {
    calculatorMobileLabel = investment.label;
  }

  const addToDataLayerHomePage = (section: string, label: string) => {
    addToDataLayer({
      overrideBase: 'Portal_Landing_Page',
      values: {
        Section: section,
        Label: label,
      },
    });
  };

  const redirectOnBoarding = (section: string, label: string) => {
    addToDataLayerHomePage(section, label);
    router.push('/onboarding');
  };

  const canRenderImg = layoutType === 'layout-image' && heroImage !== false;

  const layoutClassName = cn(styles.hero, {
    [styles.hero__modLayoutOnlyText]: layoutType === 'layout-only-text',
  });
  const descriptionClassName = cn(styles.hero__text_description, {
    [retirement.className]: isRetirement,
    [investment.className]: isInvestment,
  });

  return (
    <section className="homepage__hero">
      <div className="default-container">
        <div className={layoutClassName}>
          <div
            className={cn(styles.hero__picture, {
              [styles.hero__picture__modImage]:
                isHeroImageEnabled && hasHeroImage,
              [styles.hero__picture__modVideo]:
                isHeroImageEnabled && !hasHeroImage,
            })}>
            {canRenderImg && (
              <picture>
                {heroImage.webp && (
                  <source srcSet={heroImage.webp} type="image/webp" />
                )}
                <source srcSet={heroImage.png} type="image/png" />
                <Image
                  src={heroImage.png}
                  alt="hero"
                  width={heroImage.width}
                  height={heroImage.height}
                />
              </picture>
            )}
            {layoutType === 'layout-video' && <VideoPlayer />}
          </div>
          <div
            className={cn(styles.hero__text, {
              [styles.hero__modLayoutOnlyText]:
                layoutType === 'layout-only-text',
            })}>
            <h1>
              <b>{titleText}</b>
            </h1>
            <div>{children}</div>
            <p data-testid="description-text" className={descriptionClassName}>
              {descriptionText}
            </p>

            {layoutType !== 'layout-only-text' ? (
              <Button
                onClick={() => redirectOnBoarding('Hero', 'Find Your Advisor')}
                as="button">
                Find An Advisor
              </Button>
            ) : (
              <div className={cn(styles.hero__text_label, 'only-mobile')}>
                {calculatorMobileLabel}
                <Image
                  src="/static/img/icons/icon_down_arrow.png"
                  alt="Down arrow"
                  width={16}
                  height={17}
                />
              </div>
            )}

            <AsFeatured brandIcons={brandIcons} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
